export const LEGACY_DISPATCH_FILTERS_STORAGE_KEY = "flowtrak_dispatch_filters_v1";
export const DISPATCH_FILTERS_STORAGE_KEY_PREFIX = "flowtrak_dispatch_filters_v2";

export const DEFAULT_DISPATCH_FILTERS = Object.freeze({
  search: "",
  advisorFilter: "",
  technicianFilter: "",
  statusFilter: "",
  flagFilter: "",
  typeFilter: "",
  overdueOnly: false,
  includePickedUpVehicles: false,
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function firstNonBlank(...values) {
  for (const value of values) {
    const s = safeString(value).trim();
    if (s) return s;
  }
  return "";
}

export function parseDispatchFilterBool(value, fallback = false) {
  const v = safeString(value).trim().toLowerCase();
  if (v === "1" || v === "true" || v === "yes") return true;
  if (v === "0" || v === "false" || v === "no") return false;
  return fallback;
}

export function getDispatchFilterScope(auth) {
  const user = auth?.user || {};
  return {
    userId: firstNonBlank(user.id, user.userId, user.appUserId, user.sub),
    storeId: firstNonBlank(
      auth?.effectiveStoreId,
      auth?.activeStoreId,
      auth?.membership?.storeId,
      user.storeId,
      user.businessUnitId,
      user.business_unit_id
    ),
  };
}

export function getDispatchFilterStorageKey(scope) {
  const userId = safeString(scope?.userId).trim();
  const storeId = safeString(scope?.storeId).trim();

  if (!userId || !storeId) return "";

  return `${DISPATCH_FILTERS_STORAGE_KEY_PREFIX}:${encodeURIComponent(
    userId
  )}:${encodeURIComponent(storeId)}`;
}

function getStorage(explicitStorage) {
  if (explicitStorage) return explicitStorage;
  if (typeof window === "undefined") return null;
  return window.localStorage || null;
}

function normalizeStoredFilters(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }

  return {
    search: safeString(parsed.search).trim(),
    advisor: safeString(parsed.advisor).trim(),
    tech: safeString(parsed.tech).trim(),
    status: safeString(parsed.status).trim(),
    flag: safeString(parsed.flag).trim(),
    type: safeString(parsed.type).trim(),
    overdue: !!parsed.overdue,
    pickedUp: !!parsed.pickedUp,
  };
}

export function readStoredDispatchFilters(storageKey, explicitStorage) {
  if (!storageKey) return null;

  try {
    const storage = getStorage(explicitStorage);
    if (!storage) return null;

    const raw = storage.getItem(storageKey);
    if (!raw) return null;

    return normalizeStoredFilters(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function readInitialDispatchFilters(
  searchParams,
  storageKey,
  explicitStorage
) {
  const stored = readStoredDispatchFilters(storageKey, explicitStorage) || {};

  const getString = (key) => {
    const fromUrl = searchParams?.get?.(key);
    if (fromUrl != null) return safeString(fromUrl);
    return safeString(stored[key] || "");
  };

  const getBool = (key) => {
    const fromUrl = searchParams?.get?.(key);
    if (fromUrl != null) return parseDispatchFilterBool(fromUrl, false);
    return !!stored[key];
  };

  const tag = safeString(searchParams?.get?.("tag")).trim();

  return {
    search: tag || getString("search"),
    advisorFilter: getString("advisor"),
    technicianFilter: getString("tech"),
    statusFilter: getString("status"),
    flagFilter: getString("flag"),
    typeFilter: getString("type"),
    overdueOnly: getBool("overdue"),
    includePickedUpVehicles: getBool("pickedUp"),
  };
}

export function writeStoredDispatchFilters(
  storageKey,
  filters,
  explicitStorage
) {
  if (!storageKey) return false;

  const payload = {
    search: safeString(filters?.search).trim(),
    advisor: safeString(filters?.advisor).trim(),
    tech: safeString(filters?.tech).trim(),
    status: safeString(filters?.status).trim(),
    flag: safeString(filters?.flag).trim(),
    type: safeString(filters?.type).trim(),
    overdue: !!filters?.overdue,
    pickedUp: !!filters?.pickedUp,
  };

  try {
    const storage = getStorage(explicitStorage);
    if (!storage) return false;

    storage.setItem(storageKey, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function clearStoredDispatchFilters(storageKey, explicitStorage) {
  if (!storageKey) return false;

  try {
    const storage = getStorage(explicitStorage);
    if (!storage) return false;

    storage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
}

export function clearLegacyDispatchFilters(explicitStorage) {
  try {
    const storage = getStorage(explicitStorage);
    if (!storage) return false;

    storage.removeItem(LEGACY_DISPATCH_FILTERS_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
